/* =========================================
   Apartman Yönetim Sistemi - JavaScript
   Phase 4: Modular Architecture
   ========================================= */

// Firebase imports
import {
    db, auth, collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc,
    signInWithEmailAndPassword, signOut, onAuthStateChanged, COLLECTIONS, APP_CONFIG,
    MONTHS, MONTHS_SHORT, CATEGORY_LABELS, STATUS_LABELS, PRIORITY_LABELS
} from './firebase-config.js';

import { AppState } from './modules/state.js';
import { NotificationsService } from './features/notifications/notifications.service.js';
import { DocumentsService } from './features/documents/documents.service.js';
import {
    renderDocumentsPresenter,
    renderResidentDocumentsPresenter
} from './features/documents/documents.presenter.js';
import { AuthService } from './features/auth/auth.service.js';
import {
    updateOverviewStatsPresenter,
    updateOverviewAlertsPresenter,
    renderUpcomingMaintenancePresenter
} from './features/dashboard/dashboard.presenter.js';
import {
    destroyAllChartsPresenter,
    initResidentChartsPresenter,
    initDuesCollectionChartPresenter,
    initFinancialStatusChartPresenter,
    initIncomePieChartPresenter,
    initExpensePieChartPresenter
} from './features/dashboard/dashboard-charts.presenter.js';
import {
    renderMaintenancePresenter,
    addMaintenancePresenter,
    updateMaintenancePresenter,
    deleteMaintenancePresenter,
    editMaintenancePresenter
} from './features/maintenance/maintenance.presenter.js';
import {
    renderTasksPresenter,
    addTaskPresenter,
    updateTaskPresenter,
    deleteTaskPresenter,
    editTaskPresenter,
    viewTaskDetailPresenter
} from './features/tasks/tasks.presenter.js';
import {
    renderApartmentsPresenter,
    editApartmentPresenter,
    saveApartmentPresenter,
    viewApartmentDetailPresenter
} from './features/apartments/apartments.presenter.js';
import {
    updateFinanceSummaryPresenter,
    renderTransactionsPresenter,
    addTransactionPresenter,
    updateTransactionPresenter,
    deleteTransactionPresenter,
    editTransactionPresenter,
    exportTransactionsToExcelPresenter
} from './features/finances/finances.presenter.js';
import {
    renderBillsPresenter,
    renderResidentBillsPresenter,
    addBillPresenter,
    updateBillPresenter,
    deleteBillPresenter,
    editBillPresenter,
    viewBillPresenter
} from './features/bills/bills.presenter.js';
import {
    updateResidentDashboardPresenter,
    loadResidentProfilePresenter,
    renderResidentDuesChartPresenter,
    renderResidentMaintenancePresenter,
    renderResidentTasksPresenter
} from './features/dashboard/resident-dashboard.presenter.js';
import {
    renderDuesTablePresenter,
    toggleDuePresenter,
    setDueStatusForMonthPresenter,
    updateMonthlyDueAmountPresenter
} from './features/dues/dues.presenter.js';
import {
    renderDecisionsPresenter,
    renderRecentDecisionsPresenter,
    renderResidentDecisionsPresenter,
    renderResidentRecentDecisionsPresenter,
    addDecisionPresenter,
    updateDecisionPresenter,
    deleteDecisionPresenter,
    editDecisionPresenter,
    viewDecisionDetailPresenter
} from './features/decisions/decisions.presenter.js';
import {
    updateNotificationPreviewPresenter,
    populateApartmentCheckboxesPresenter,
    renderNotificationHistoryPresenter,
    handleRecipientTypeChangePresenter,
    submitNotificationFormPresenter
} from './features/notifications/notifications.presenter.js';
import { registerAppBindings, registerVisualEffectBindings } from './core/app-bindings.js';
import { registerWindowBindings } from './core/global-bindings.js';
import { refreshSectionOrchestrator } from './core/section-orchestrator.js';
import { FirebaseService } from './shared/services/firebase.service.js';
import {
    openModal,
    closeModal,
    closeAllModals,
    setMenuState
} from './shared/ui/runtime-modal.js';
import {
    showToast,
    showSuccessToast,
    showErrorToast,
    showLoadingToast
} from './shared/ui/runtime-toast.js';
import {
    generateId,
    formatNumber,
    formatDate,
    escapeHtml,
    fileToBase64
} from './shared/utils/runtime-utils.js';

interface Window {
    db: any;
    loginAdmin: any;
    loginResident: any;
    logout: any;
    showSection: any;
    editTransaction: any;
    deleteTransaction: any;
    editBill: any;
    viewBill: any;
    deleteBill: any;
    toggleDue: any;
    editDecision: any;
    deleteDecision: any;
    viewDecisionDetail: any;
    editMaintenance: any;
    deleteMaintenance: any;
    editTask: any;
    deleteTask: any;
    viewTaskDetail: any;
    editApartment: any;
    renderBills: any;
    renderDuesTable: any;
    updateMonthlyDueAmount: any;
    renderTransactions: any;
    updateFinanceSummary: any;
    renderMaintenance: any;
    renderDecisions: any;
    renderRecentDecisions: any;
    renderTasks: any;
    closeAllModals: any;
    migrateData: any;
}

(window as any).db = db; // Debug purposes

// NOTE: Admin authentication is handled by Firebase Auth - no hardcoded passwords
// NOTE: Module files exist in js/modules/ for future gradual migration:
//       - config.js, state.js, utils.js, ui.js, firebase-service.js
const TOTAL_APARTMENTS = APP_CONFIG.TOTAL_APARTMENTS;

// ===== Theme Management =====
function initTheme() {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    if (savedTheme) {
        document.documentElement.setAttribute('data-theme', savedTheme);
    } else if (prefersDark) {
        document.documentElement.setAttribute('data-theme', 'dark');
    }
}

function toggleTheme() {
    const currentTheme = document.documentElement.getAttribute('data-theme');
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';

    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
}

// Initialize theme immediately to prevent flash
initTheme();

// ===== Custom Cursor =====
function initCustomCursor() {
    const dot = document.querySelector('.cursor-dot');
    const outline = document.querySelector('.cursor-outline');

    if (!dot || !outline) return;

    let mouseX = 0, mouseY = 0;
    let outlineX = 0, outlineY = 0;

    // Update cursor position on mouse move
    document.addEventListener('mousemove', (e) => {
        mouseX = e.clientX;
        mouseY = e.clientY;
        (dot as HTMLElement).style.left = mouseX + 'px';
        (dot as HTMLElement).style.top = mouseY + 'px';
    });

    // Smooth outline following
    function animateOutline() {
        outlineX += (mouseX - outlineX) * 0.15;
        outlineY += (mouseY - outlineY) * 0.15;
        (outline as HTMLElement).style.left = outlineX + 'px';
        (outline as HTMLElement).style.top = outlineY + 'px';
        requestAnimationFrame(animateOutline);
    }
    animateOutline();

    // Hover effect on interactive elements
    const hoverElements = document.querySelectorAll('a, button, input, select, .card, .glass-card, .nav-link');
    hoverElements.forEach(el => {
        el.addEventListener('mouseenter', () => {
            dot.classList.add('hover');
            outline.classList.add('hover');
        });
        el.addEventListener('mouseleave', () => {
            dot.classList.remove('hover');
            outline.classList.remove('hover');
        });
    });

    // Hide cursor on mobile
    if ('ontouchstart' in window) {
        (dot as HTMLElement).style.display = 'none';
        (outline as HTMLElement).style.display = 'none';
    }
}

// Initialize cursor after DOM load
document.addEventListener('DOMContentLoaded', initCustomCursor);

// ===== Skeleton Loading Helpers =====
function createSkeletonCards(count = 3, type = 'stat') {
    let html = '';
    for (let i = 0; i < count; i++) {
        if (type === 'stat') {
            html += `
                <div class="skeleton-stat skeleton-pulse">
                    <div class="skeleton skeleton-icon"></div>
                    <div class="skeleton skeleton-value"></div>
                    <div class="skeleton skeleton-label"></div>
                </div>`;
        } else if (type === 'card') {
            html += `
                <div class="skeleton-card skeleton-pulse">
                    <div class="skeleton skeleton-title"></div>
                    <div class="skeleton skeleton-text"></div>
                    <div class="skeleton skeleton-text"></div>
                </div>`;
        } else if (type === 'list') {
            html += `
                <div class="skeleton-list-item skeleton-pulse">
                    <div class="skeleton skeleton-circle"></div>
                    <div class="skeleton-content">
                        <div class="skeleton skeleton-line"></div>
                        <div class="skeleton skeleton-line"></div>
                    </div>
                </div>`;
        }
    }
    return html;
}

function showSkeleton(containerId, count = 3, type = 'stat') {
    const container = document.getElementById(containerId);
    if (container) {
        container.innerHTML = `<div class="skeleton-grid">${createSkeletonCards(count, type)}</div>`;
    }
}

function hideSkeleton(containerId) {
    const container = document.getElementById(containerId);
    if (container) {
        const skeleton = container.querySelector('.skeleton-grid');
        if (skeleton) skeleton.remove();
    }
}

// ===== State Management =====
// AppState is imported from ./modules/state.js

// Legacy Storage Adapter (for compatibility)
// We use this to persist local changes to Firestore asynchronously
const Storage = {
    save(key, data) {
        // 'data' here is usually the full array/object. 
        // For Firestore, we typically update individual items.
        // However, for parts of the app that rely on saving the *entire* state (like settings),
        // we can save to a single document or treat it differently.

        // Strategy: We won't use Storage.save for massive arrays anymore.
        // We will update individual items in the respective functions (addTransaction, etc).
        // Only 'settings' and 'dues' might need special handling.

        if (key === 'settings') {
            FirebaseService.save(COLLECTIONS.SETTINGS, 'config', data);
        } else if (key === 'dues') {
            // For dues, 'data' is the whole year structure.
            // We should save per year.
            const year = AppState.currentYear;
            if (data[year]) {
                FirebaseService.save(COLLECTIONS.DUES, year.toString(), data[year]);
            }
        }
    },
    load(key, defaultValue) { return defaultValue; }, // Deprecated for direct use
    remove(key) { }
};

// ===== Initialize Data =====
async function initializeData() {
    showToast('Veriler yükleniyor...', 'info');

    try {
        // Load in parallel
        const [
            bills, decisions, transactions, maintenance, tasks, settingsDoc, currentDuesDoc, apartments
        ] = await Promise.all([
            FirebaseService.loadCollection(COLLECTIONS.BILLS),
            FirebaseService.loadCollection(COLLECTIONS.DECISIONS),
            FirebaseService.loadCollection(COLLECTIONS.TRANSACTIONS),
            FirebaseService.loadCollection(COLLECTIONS.MAINTENANCE),
            FirebaseService.loadCollection(COLLECTIONS.TASKS),
            getDoc(doc(db, COLLECTIONS.SETTINGS, 'config')),
            getDoc(doc(db, COLLECTIONS.DUES, AppState.currentYear.toString())),
            FirebaseService.loadCollection(COLLECTIONS.APARTMENTS)
        ]);

        AppState.bills = bills;
        AppState.decisions = decisions;
        AppState.transactions = transactions;
        AppState.maintenance = maintenance;
        AppState.tasks = tasks;
        AppState.apartments = apartments;

        // Initialize settings if not exists
        if (settingsDoc.exists()) {
            AppState.settings = settingsDoc.data() as any;
        } else {
            // Save default settings to Firebase
            AppState.settings = { monthlyDueAmount: 500 };
            await FirebaseService.save(COLLECTIONS.SETTINGS, 'config', AppState.settings);
        }

        // Initialize Notifications Service
        await NotificationsService.initialize();

        // Initialize Documents Service
        await DocumentsService.initialize();

        // Load notification settings (GREEN-API config)
        try {
            const notificationSettingsDoc = await getDoc(doc(db, 'settings', 'notifications'));
            if (notificationSettingsDoc.exists()) {
                const notifSettings = notificationSettingsDoc.data();
                AppState.settings.greenApiIdInstance = notifSettings.greenApiIdInstance || '';
                AppState.settings.greenApiToken = notifSettings.greenApiToken || '';

                // Populate input fields after DOM is ready
                setTimeout(() => {
                    const instanceInput = document.getElementById('greenapi-instance') as HTMLInputElement;
                    const tokenInput = document.getElementById('greenapi-token') as HTMLInputElement;
                    if (instanceInput && AppState.settings.greenApiIdInstance) {
                        instanceInput.value = AppState.settings.greenApiIdInstance;
                    }
                    if (tokenInput && AppState.settings.greenApiToken) {
                        tokenInput.value = AppState.settings.greenApiToken;
                    }
                }, 100);
            }
        } catch (e) {
            console.warn('Could not load notification settings:', e);
        }

        // Initialize dues for current year
        AppState.dues = {};
        if (currentDuesDoc.exists()) {
            AppState.dues[AppState.currentYear] = currentDuesDoc.data();
        } else {
            // Initialize empty dues for current year and save to Firebase
            AppState.dues[AppState.currentYear] = {};
            for (let apt = 1; apt <= TOTAL_APARTMENTS; apt++) {
                AppState.dues[AppState.currentYear][apt] = {};
                for (let month = 1; month <= 12; month++) {
                    AppState.dues[AppState.currentYear][apt][month] = false;
                }
            }
            // Save initial dues to Firebase
            await FirebaseService.save(COLLECTIONS.DUES, AppState.currentYear.toString(), AppState.dues[AppState.currentYear]);
        }

        // Initialize missing apartments (ensure all 12 exist in Firebase)
        await initializeMissingApartments(apartments);

        refreshCurrentView();
        showToast('Veriler güncellendi', 'success');

    } catch (error) {
        console.error("Init Error:", error);
        showToast('Veri yükleme hatası. Lütfen internet bağlantınızı kontrol edip sayfayı yenileyin.', 'error');
    }
}

/**
 * Login ekranı için minimum veriyi yükler.
 * Amaç: anonim kullanıcıda permission hatası üretmeden sakin girişinin çalışması.
 */
async function initializePublicData() {
    try {
        const apartments = await FirebaseService.loadCollection(COLLECTIONS.APARTMENTS);
        AppState.apartments = apartments;
    } catch (error) {
        console.error('Public data init error:', error);
        showToast('Daire listesi yüklenemedi. Sayfayı yenileyin.', 'error');
    }
}

/**
 * Sakin ekranı için gerekli verileri, permission hatasında kırılmadan yükler.
 */
async function initializeResidentData() {
    const safeLoadCollection = async (colName: string, fallback: any[] = []) => {
        try {
            return await FirebaseService.loadCollection(colName, { silent: true });
        } catch (e: any) {
            if (e?.code === 'permission-denied' || e?.code === 'firestore/permission-denied') {
                return fallback;
            }
            throw e;
        }
    };

    const safeGetDoc = async (docRef: any) => {
        try {
            return await getDoc(docRef);
        } catch (e: any) {
            if (e?.code === 'permission-denied' || e?.code === 'firestore/permission-denied') {
                return null;
            }
            throw e;
        }
    };

    try {
        const [apartments, decisions, maintenance, tasks, bills, transactions, currentDuesDoc] = await Promise.all([
            safeLoadCollection(COLLECTIONS.APARTMENTS),
            safeLoadCollection(COLLECTIONS.DECISIONS),
            safeLoadCollection(COLLECTIONS.MAINTENANCE),
            safeLoadCollection(COLLECTIONS.TASKS),
            safeLoadCollection(COLLECTIONS.BILLS),
            safeLoadCollection(COLLECTIONS.TRANSACTIONS),
            safeGetDoc(doc(db, COLLECTIONS.DUES, AppState.currentYear.toString()))
        ]);

        AppState.apartments = apartments;
        AppState.decisions = decisions;
        AppState.maintenance = maintenance;
        AppState.tasks = tasks;
        AppState.bills = bills;
        AppState.transactions = transactions;

        AppState.dues = AppState.dues || {};
        if (currentDuesDoc && currentDuesDoc.exists()) {
            AppState.dues[AppState.currentYear] = currentDuesDoc.data();
        } else if (!AppState.dues[AppState.currentYear]) {
            AppState.dues[AppState.currentYear] = {};
        }
    } catch (error) {
        console.error('Resident data init error:', error);
    }
}

// Initialize missing apartments in Firebase
async function initializeMissingApartments(existingApartments) {
    const existingNumbers = new Set(existingApartments.map(a => a.number));
    const missingApartments = [];

    for (let i = 1; i <= TOTAL_APARTMENTS; i++) {
        if (!existingNumbers.has(i)) {
            const aptData = {
                number: i,
                residentName: '',
                phone: '',
                status: 'empty',
                ownerName: '',
                residentCount: 0,
                moveInDate: '',
                createdAt: new Date().toISOString()
            };
            missingApartments.push(aptData);
        }
    }

    // Save missing apartments to Firebase in parallel
    if (missingApartments.length > 0) {
        console.log(`Initializing ${missingApartments.length} missing apartments...`);
        const savePromises = missingApartments.map(async (apt) => {
            const docId = `apt_${apt.number}`;
            apt.id = docId;
            await setDoc(doc(db, COLLECTIONS.APARTMENTS, docId), apt);
            return apt;
        });

        const savedApartments = await Promise.all(savePromises);
        AppState.apartments = [...existingApartments, ...savedApartments].sort((a, b) => a.number - b.number);
        console.log('All apartments initialized in Firebase');
    }
}

function refreshCurrentView() {
    if (AppState.currentPage) {
        showSection(AppState.currentSection);
    }
}

// ===== Page Navigation =====
function showPage(pageId) {
    document.querySelectorAll('.page').forEach(page => page.classList.remove('active'));
    document.getElementById(pageId).classList.add('active');
    AppState.currentPage = pageId;

    const skipLink = document.getElementById('skip-to-main-link') as HTMLAnchorElement | null;
    if (skipLink) {
        skipLink.setAttribute('href', pageId === 'resident-dashboard' ? '#resident-main-content' : '#main-content');
    }
}

function showSection(sectionId) {
    const dashboard = AppState.currentUser?.role === 'admin' ? 'admin-dashboard' : 'resident-dashboard';
    const container = document.getElementById(dashboard);

    container.querySelectorAll('.section').forEach(s => s.classList.remove('active'));
    const section = document.getElementById(`${sectionId}-section`);
    if (section) section.classList.add('active');

    container.querySelectorAll('.nav-link').forEach(link => {
        link.classList.remove('active');
        if ((link as HTMLElement).dataset.section === sectionId) link.classList.add('active');
    });

    if (dashboard === 'resident-dashboard') {
        document.querySelectorAll('#resident-tab-bar .tab-item[data-section]').forEach(tab => {
            tab.classList.toggle('active', (tab as HTMLElement).dataset.section === sectionId);
        });
    }

    AppState.currentSection = sectionId;
    refreshSection(sectionId);
}

function refreshSection(sectionId) {
    refreshSectionOrchestrator(sectionId, getSectionRefreshDeps());
}

// ===== Authentication (Using AuthService) =====
async function loginAdmin(password: string, adminEmail?: string): Promise<boolean> {
    const success = await AuthService.loginAdmin(password, adminEmail);
    if (success) {
        showPage('admin-dashboard');
        await initializeData();
        showSection('overview');
    }
    return success;
}

async function loginResident(apartmentNumber: number, password: string): Promise<boolean> {
    const success = await AuthService.loginResident(apartmentNumber, password);
    if (success) {
        document.getElementById('resident-apartment-badge')!.textContent = `Daire ${apartmentNumber}`;
        (document.getElementById('resident-welcome-text') as HTMLElement).textContent = `Daire ${apartmentNumber} - Apartman bilgileri ve durumu`;
        initializeResidentData()
            .catch(error => console.error('Resident login data init error:', error))
            .finally(() => {
                showPage('resident-dashboard');
                showSection('resident-overview');
            });
    }
    return success;
}

async function logout(): Promise<void> {
    await AuthService.logout();
    destroyAllCharts();
}

function checkAuth(): void {
    AuthService.checkAuth(async (user) => {
        if (user) {
            if (AppState.currentPage === 'login-page') {
                if (user.role === 'admin') {
                    showPage('admin-dashboard');
                    await initializeData();
                    showSection('overview');
                } else if (user.role === 'resident') {
                    const apt = (user as any).apartment;
                    document.getElementById('resident-apartment-badge')!.textContent = `Daire ${apt}`;
                    (document.getElementById('resident-welcome-text') as HTMLElement).textContent = `Daire ${apt} - Apartman bilgileri ve durumu`;
                    await initializeResidentData();
                    showPage('resident-dashboard');
                    showSection('resident-overview');
                }
            }
        } else {
            showPage('login-page');
        }
    });
}

// ===== Security Helpers =====

/**
 * Verify admin access before sensitive operations
 * Throws error if not admin
 */
function requireAdmin(): void {
    if (!AuthService.isAdmin()) {
        console.error('❌ Unauthorized access attempt');
        showToast('Bu işlem için yönetici yetkisi gereklidir.', 'error');
        throw new Error('Admin access required');
    }
}

/**
 * Verify access to apartment data
 */
function requireApartmentAccess(apartmentNumber: number): boolean {
    if (AuthService.isAdmin()) return true;
    if (AuthService.isResident() && AuthService.getResidentApartment() === apartmentNumber) {
        return true;
    }
    showToast('Bu daireye erişim yetkiniz yok.', 'error');
    return false;
}

/**
 * Wrap async functions with admin check
 */
function withAdminCheck<T extends (...args: any[]) => Promise<any>>(fn: T): T {
    return (async (...args: Parameters<T>): Promise<ReturnType<T>> => {
        requireAdmin();
        return fn(...args);
    }) as T;
}


// ===== Overview Stats =====
function updateOverviewStats() {
    updateOverviewStatsPresenter();
}

function updateOverviewAlerts() {
    updateOverviewAlertsPresenter();
}

function renderUpcomingMaintenance() {
    renderUpcomingMaintenancePresenter(getDashboardPresenterDeps());
}

// ===== Transactions Management =====
function getFinancePresenterDeps() {
    return {
        openModal,
        showToast,
        generateId,
        escapeHtml
    };
}

function updateFinanceSummary() {
    updateFinanceSummaryPresenter();
}

function renderTransactions() {
    renderTransactionsPresenter(getFinancePresenterDeps());
}

function exportTransactionsToExcel() {
    exportTransactionsToExcelPresenter(getFinancePresenterDeps());
}

async function addTransaction(data) {
    return addTransactionPresenter(data, getFinancePresenterDeps());
}

async function updateTransaction(id, data) {
    return updateTransactionPresenter(id, data, getFinancePresenterDeps());
}

function deleteTransaction(id) {
    deleteTransactionPresenter(id, getFinancePresenterDeps());
}

function editTransaction(id) {
    editTransactionPresenter(id, getFinancePresenterDeps());
}

// ===== Maintenance Management =====
function getMaintenancePresenterDeps() {
    return { openModal, showToast, generateId, escapeHtml };
}

function renderMaintenance() {
    renderMaintenancePresenter(getMaintenancePresenterDeps());
}

function addMaintenance(data) {
    return addMaintenancePresenter(data, getMaintenancePresenterDeps());
}

function updateMaintenance(id, data) {
    return updateMaintenancePresenter(id, data, getMaintenancePresenterDeps());
}

function deleteMaintenance(id) {
    deleteMaintenancePresenter(id, getMaintenancePresenterDeps());
}

function editMaintenance(id) {
    editMaintenancePresenter(id, getMaintenancePresenterDeps());
}

// ===== Tasks Management =====
function getTasksPresenterDeps() {
    return { openModal, showToast, generateId, escapeHtml };
}

function renderTasks() {
    renderTasksPresenter(getTasksPresenterDeps());
}

function addTask(data) {
    return addTaskPresenter(data, getTasksPresenterDeps());
}

function updateTask(id, data) {
    return updateTaskPresenter(id, data, getTasksPresenterDeps());
}

function deleteTask(id) {
    deleteTaskPresenter(id, getTasksPresenterDeps());
}

function editTask(id) {
    editTaskPresenter(id, getTasksPresenterDeps());
}

function viewTaskDetail(id) {
    viewTaskDetailPresenter(id, getTasksPresenterDeps());
}

// ===== Bills Management =====
function getBillsPresenterDeps() {
    return {
        openModal,
        showToast,
        generateId,
        escapeHtml
    };
}

function renderBills() {
    renderBillsPresenter(getBillsPresenterDeps());
}

function renderResidentBills() {
    renderResidentBillsPresenter();
}

function addBill(data) {
    return addBillPresenter(data, getBillsPresenterDeps());
}

function updateBill(id, data) {
    return updateBillPresenter(id, data, getBillsPresenterDeps());
}

function deleteBill(id) {
    deleteBillPresenter(id, getBillsPresenterDeps());
}

function editBill(id) {
    editBillPresenter(id, getBillsPresenterDeps());
}

function viewBill(id) {
    viewBillPresenter(id, getBillsPresenterDeps());
}

// ===== Dues Management =====
function getDuesPresenterDeps() {
    return {
        totalApartments: TOTAL_APARTMENTS,
        formatNumber,
        showToast,
        renderTransactions,
        updateFinanceSummary
    };
}

function renderDuesTable() {
    renderDuesTablePresenter(getDuesPresenterDeps());
}

async function toggleDue(year, apartment, month) {
    await toggleDuePresenter(year, apartment, month, getDuesPresenterDeps());
}

async function setDueStatusForMonth(isPaid) {
    await setDueStatusForMonthPresenter(isPaid, getDuesPresenterDeps());
}

const updateMonthlyDueAmount = async () => {
    await updateMonthlyDueAmountPresenter(getDuesPresenterDeps());
}

// ===== Decisions Management =====
function getDecisionsPresenterDeps() {
    return {
        openModal,
        showToast,
        generateId,
        escapeHtml
    };
}

function getResidentDashboardPresenterDeps() {
    return {
        escapeHtml,
        renderResidentRecentDecisions,
        initResidentCharts
    };
}

function getNotificationsPresenterDeps() {
    return {
        closeModal,
        showToast,
        onSelectionChanged: updateNotificationPreview
    };
}

function getDashboardPresenterDeps() {
    return {
        escapeHtml
    };
}

function getDashboardChartsPresenterDeps() {
    return {
        formatNumber
    };
}

function getSectionRefreshDeps() {
    return {
        updateOverviewStats,
        updateOverviewAlerts,
        renderUpcomingMaintenance,
        renderRecentDecisions,
        renderTransactions,
        updateFinanceSummary,
        renderBills,
        renderDuesTable,
        renderMaintenance,
        renderTasks,
        renderDecisions,
        renderApartments,
        updateResidentDashboard,
        renderResidentBills,
        renderResidentDecisions,
        renderResidentDocuments,
        loadResidentProfile,
        renderNotificationHistory,
        renderDocuments
    };
}

function renderDecisions() {
    renderDecisionsPresenter(getDecisionsPresenterDeps());
}

function renderRecentDecisions() {
    renderRecentDecisionsPresenter(getDecisionsPresenterDeps());
}

function renderResidentDecisions() {
    renderResidentDecisionsPresenter(getDecisionsPresenterDeps());
}

function renderResidentDocuments() {
    renderResidentDocumentsPresenter();
}

function addDecision(data) {
    return addDecisionPresenter(data, getDecisionsPresenterDeps());
}

function updateDecision(id, data) {
    return updateDecisionPresenter(id, data, getDecisionsPresenterDeps());
}

function deleteDecision(id) {
    deleteDecisionPresenter(id, getDecisionsPresenterDeps());
}

function editDecision(id) {
    editDecisionPresenter(id, getDecisionsPresenterDeps());
}

// ===== Apartments Management (NEW) =====
function getApartmentsPresenterDeps() {
    return {
        openModal,
        closeModal,
        showToast,
        escapeHtml,
        totalApartments: TOTAL_APARTMENTS
    };
}

function renderApartments() {
    renderApartmentsPresenter(getApartmentsPresenterDeps());
}

function editApartment(id) {
    editApartmentPresenter(id, getApartmentsPresenterDeps());
}

async function saveApartment(data) {
    await saveApartmentPresenter(data, getApartmentsPresenterDeps());
}

function viewApartmentDetail(id) {
    viewApartmentDetailPresenter(id, getApartmentsPresenterDeps());
}

async function migrateData() {
    if (!confirm("Mevcut tarayıcı verileri Firebase'e aklatılacak. Emin misiniz?")) return;

    showToast('Veriler aktarılıyor...', 'info');

    try {
        // 1. Transactions
        const transactions = JSON.parse(localStorage.getItem('apt_transactions') || '[]');
        for (const t of transactions) {
            await FirebaseService.save(COLLECTIONS.TRANSACTIONS, t.id, t);
        }

        // 2. Bills
        const bills = JSON.parse(localStorage.getItem('apt_bills') || '[]');
        for (const b of bills) {
            await FirebaseService.save(COLLECTIONS.BILLS, b.id, b);
        }

        // 3. Decisions
        const decisions = JSON.parse(localStorage.getItem('apt_decisions') || '[]');
        for (const d of decisions) {
            await FirebaseService.save(COLLECTIONS.DECISIONS, d.id, d);
        }

        // 4. Maintenance
        const maintenance = JSON.parse(localStorage.getItem('apt_maintenance') || '[]');
        for (const m of maintenance) {
            await FirebaseService.save(COLLECTIONS.MAINTENANCE, m.id, m);
        }

        // 5. Tasks
        const tasks = JSON.parse(localStorage.getItem('apt_tasks') || '[]');
        for (const t of tasks) {
            await FirebaseService.save(COLLECTIONS.TASKS, t.id, t);
        }

        showToast('Aktarım tamamlandı! Sayfa yenileniyor...', 'success');
        setTimeout(() => location.reload(), 1500);

    } catch (e) {
        console.error("Migration failed", e);
        showToast('Aktarım sırasında hata oluştu', 'error');
    }
}

// ===== Resident Dashboard =====
// ===== Resident Overview - Enhanced Dashboard (Updates)
function updateResidentDashboard() {
    updateResidentDashboardPresenter(getResidentDashboardPresenterDeps());
}

// Load Resident Profile Data
function loadResidentProfile() {
    loadResidentProfilePresenter();
}

function renderResidentDuesChart(year, apt) {
    renderResidentDuesChartPresenter(year, apt);
}

function renderResidentMaintenance() {
    renderResidentMaintenancePresenter(getResidentDashboardPresenterDeps());
}

function renderResidentTasks() {
    renderResidentTasksPresenter(getResidentDashboardPresenterDeps());
}

function renderResidentRecentDecisions() {
    renderResidentRecentDecisionsPresenter(getDecisionsPresenterDeps());
}

// View Decision Detail in Modal
function viewDecisionDetail(id) {
    viewDecisionDetailPresenter(id, getDecisionsPresenterDeps());
}

// ===== Charts =====
function destroyAllCharts() {
    destroyAllChartsPresenter();
}

function initResidentCharts() {
    initResidentChartsPresenter(getDashboardChartsPresenterDeps());
}

function initDuesCollectionChart() {
    initDuesCollectionChartPresenter();
}

function initFinancialStatusChart() {
    initFinancialStatusChartPresenter(getDashboardChartsPresenterDeps());
}

function initIncomePieChart() {
    initIncomePieChartPresenter();
}

function initExpensePieChart() {
    initExpensePieChartPresenter();
}

// ===== Document Management Rendering =====
function renderDocuments() {
    renderDocumentsPresenter();
}

// ===== Notification Helper Functions =====
function updateNotificationPreview() {
    updateNotificationPreviewPresenter();
}

function handleNotificationRecipientChange() {
    handleRecipientTypeChangePresenter(getNotificationsPresenterDeps());
}

async function submitNotificationForm() {
    await submitNotificationFormPresenter(getNotificationsPresenterDeps());
}

function populateApartmentCheckboxes() {
    populateApartmentCheckboxesPresenter(getNotificationsPresenterDeps());
}

// Render Notification History
function renderNotificationHistory() {
    renderNotificationHistoryPresenter();
}

function getAppBindingsDeps() {
    return {
        initializePublicData,
        checkAuth,
        adminEmail: APP_CONFIG.ADMIN_EMAIL || '',
        totalApartments: TOTAL_APARTMENTS,
        showToast,
        toggleTheme,
        showSection,
        setMenuState,
        renderTasks,
        openModal,
        closeModal,
        closeAllModals,
        renderBills,
        renderTransactions,
        updateFinanceSummary,
        exportTransactionsToExcel,
        renderDuesTable,
        updateMonthlyDueAmount,
        setDueStatusForMonth,
        renderApartments,
        saveApartment,
        fileToBase64,
        updateBill,
        addBill,
        updateDecision,
        addDecision,
        renderDecisions,
        renderRecentDecisions,
        renderResidentDecisions,
        renderResidentRecentDecisions,
        updateTransaction,
        addTransaction,
        updateMaintenance,
        addMaintenance,
        renderMaintenance,
        updateTask,
        addTask,
        logout,
        loginAdmin,
        loginResident,
        migrateData,
        updateNotificationPreview,
        handleNotificationRecipientChange,
        submitNotificationForm,
        renderDocuments,
        renderResidentDocuments
    };
}

registerWindowBindings({
    db,
    loginAdmin,
    loginResident,
    logout,
    showSection,
    editTransaction,
    deleteTransaction,
    editBill,
    viewBill,
    deleteBill,
    toggleDue,
    editDecision,
    deleteDecision,
    viewDecisionDetail,
    editMaintenance,
    deleteMaintenance,
    editTask,
    deleteTask,
    viewTaskDetail,
    editApartment,
    viewApartmentDetail,
    renderBills,
    renderDuesTable,
    updateMonthlyDueAmount,
    renderTransactions,
    updateFinanceSummary,
    renderMaintenance,
    renderDecisions,
    renderRecentDecisions,
    renderTasks,
    closeAllModals,
    migrateData
});

registerAppBindings(getAppBindingsDeps());
registerVisualEffectBindings();
